import {
    SINGLE_GROUP_TIME_SLICE_IN_SECONDS,
    TIME_FRAME_TO_TIME_SLICE_SIZE_MULTIPLIER_FOR_ACCUMULATED_CHARTS,
} from 'app/overview/dashboard/config/time_slice';
import {
    DashboardItemService,
    DataSourceResource,
} from 'app/overview/dashboard/dashboard_item.service';
import { isNil } from 'lodash';

/**
 * Check is a resource is recommended in the current context.
 * This is used when calculating an automatic time slice size, the time slice size can be checked against each resource to see
 * if it's recommenced.
 */
export function isResourceRecommended(
    res: DataSourceResource,
    timeSliceInSeconds: number,
    timeFrameInSeconds: number,
): boolean {
    // make sure the resource is usable, and
    // make sure the total time frame in less than the recommended max supported time frame for that resource or there is no recommended max for that resource
    return (
        isResourceUsable(res, timeSliceInSeconds, timeFrameInSeconds) &&
        (isNil(res.recommendedMaxTimeFrameSeconds) ||
            res.recommendedMaxTimeFrameSeconds >= timeFrameInSeconds)
    );
}

/**
 * Check if a resource is valid in the current context.
 */
export function isResourceUsable(
    res: DataSourceResource,
    timeSliceInSeconds: number,
    timeFrameInSeconds: number,
): boolean {
    // make sure the resource aggregation seconds is equal or smaller than the time slice size (at least 1 stat per slice)
    const actualSliceSize =
        timeSliceInSeconds === SINGLE_GROUP_TIME_SLICE_IN_SECONDS
            ? timeFrameInSeconds / TIME_FRAME_TO_TIME_SLICE_SIZE_MULTIPLIER_FOR_ACCUMULATED_CHARTS
            : timeSliceInSeconds;
    return (
        res.aggSeconds <= actualSliceSize &&
        (isNil(res.maxTimeFrameSeconds) || timeFrameInSeconds <= res.maxTimeFrameSeconds)
    );
}

export function autoSelectResource(
    dataSource: string,
    timeFrameInSeconds: number,
    timeSliceSizeInSeconds: number,
): DataSourceResource {
    const resources = getResourceAggregations(dataSource);
    // sort list by seconds descending, meaning biggest aggregations first
    const sorted = resources.sort((a, b) => b.aggSeconds - a.aggSeconds);
    // then just find the first suitable resource
    return sorted.find((res) => isResourceUsable(res, timeSliceSizeInSeconds, timeFrameInSeconds));
}

export function getResourceAggregations(dataSource: string): Array<DataSourceResource> {
    const resourceDefinitionForDataSource =
        DashboardItemService.getResourceDefinitionForDataSource(dataSource);
    return [
        {
            name: resourceDefinitionForDataSource.default,
            aggSeconds: 0,
            recommendedMaxTimeFrameSeconds:
                resourceDefinitionForDataSource.defaultRecommendedMaxTimeFrameSeconds,
            maxTimeFrameSeconds: resourceDefinitionForDataSource.defaultMaxTimeFrameSeconds,
        },
        ...resourceDefinitionForDataSource.preAggregated,
    ];
}
